import { useNavigate, useParams } from "react-router-dom";
import { AnalyticsLayout } from "../components/layout/AnalyticsLayout";
import { SummaryTiles } from "../components/analytics/SummaryTiles";
import { ActivityTable } from "../components/analytics/ActivityTable";
import { Spinner } from "../components/ui/Spinner";
import { useActivityByUser } from "../hooks/useActivity";
import { formatDateTime } from "../lib/format";

export function AnalyticsUserPage() {
  const navigate = useNavigate();
  const { userId = "" } = useParams();
  const { data, isLoading, isError } = useActivityByUser(userId);

  return (
    <AnalyticsLayout>
      <button
        type="button"
        onClick={() => navigate("/analytics")}
        className="text-body font-medium text-brand-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
      >
        ← Back to Analytics
      </button>

      {isLoading ? (
        <div className="mt-6 flex h-64 items-center justify-center">
          <Spinner className="h-8 w-8 text-brand-600" label="Loading" />
        </div>
      ) : isError || !data ? (
        <div className="mt-6 rounded-lg border border-dashed border-surface-border bg-surface p-8 text-center text-body text-text-muted">
          Investor not found.
        </div>
      ) : (
        <div className="mt-4 flex flex-col gap-6">
          <div>
            <h1 className="text-title font-semibold text-text">{data.user.displayName}</h1>
            <p className="mt-1 text-body text-text-muted">
              {data.summary.firstSeen
                ? `First seen ${formatDateTime(data.summary.firstSeen)} · Last active ${
                    data.summary.lastActive ? formatDateTime(data.summary.lastActive) : "—"
                  }`
                : "No activity yet."}
            </p>
          </div>

          <SummaryTiles
            tiles={[
              { label: "Total opens", value: data.summary.totalOpens },
              { label: "Total downloads", value: data.summary.totalDownloads },
              { label: "Folders touched", value: data.rollup.length },
            ]}
          />

          {data.rollup.length > 0 ? (
            <section className="flex flex-col gap-3">
              <h2 className="text-body font-semibold text-text">By folder</h2>
              <div className="overflow-hidden rounded-lg border border-surface-border bg-surface">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-surface-border text-meta text-text-muted">
                      <th className="px-3 py-2 font-medium">Folder</th>
                      <th className="px-3 py-2 font-medium">Opens</th>
                      <th className="px-3 py-2 font-medium">Downloads</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rollup.map((r) => (
                      <tr key={r.folder} className="border-b border-surface-border last:border-0">
                        <td className="px-3 py-2 text-body text-text">{r.folder}</td>
                        <td className="px-3 py-2 text-body text-brand-700">{r.opens}</td>
                        <td className="px-3 py-2 text-body text-success-700">{r.downloads}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ) : null}

          <section className="flex flex-col gap-3">
            <h2 className="text-body font-semibold text-text">Activity</h2>
            <ActivityTable
              rows={data.events}
              total={data.events.length}
              page={1}
              pageSize={Math.max(data.events.length, 1)}
              onPageChange={() => {}}
              onSelectFile={(fileId) => navigate(`/analytics/files/${fileId}`)}
              emptyCopy="This investor hasn't opened any files yet."
            />
          </section>
        </div>
      )}
    </AnalyticsLayout>
  );
}
